import type { AssessmentCadence } from '@/types/models';
import { parBpsToMicroPct } from '@/utils/par-value';

/**
 * The only account fields dues resolution reads. Declared structurally so both
 * the client (`Account`, camelCase) and the Worker's dues cron (a snake_case D1
 * row, mapped through a small adapter) can call the SAME resolver. There was
 * previously a hand-copied twin of this logic in `api/cron/accounting.ts`; the
 * UI previewed one number while the cron posted another whenever the two drifted.
 */
export interface AccountDuesInput {
	duesOverrideAmountCents?: number | null;
	duesOverridePercentBps?: number | null;
	/** Ownership share in basis points — FRACTIONAL (136.6 = 1.366%); see utils/par-value.ts. */
	parValueBps?: number | null;
}

/**
 * How par-value dues are rounded:
 *  - `none`    — to the cent.
 *  - `nearest` — each GL line to the nearest dollar (half up); the bill is the SUM of the
 *                rounded lines, which is how the offices bill ($262 + $52 = $314).
 *  - `up`      — each GL line up to the next dollar (the old #232 behaviour).
 */
export const DUES_ROUNDINGS = ['none', 'nearest', 'up'] as const;
export type DuesRounding = (typeof DUES_ROUNDINGS)[number];

/** Narrow a stored `associations.dues_rounding` value. */
export const isDuesRounding = (v: unknown): v is DuesRounding => (DUES_ROUNDINGS as readonly unknown[]).includes(v);

export interface AssociationDues {
	amountCents?: number;
	cadence?: AssessmentCadence;
	dayOfMonth?: number;
	startOn?: number;
	/** Par-value mode toggle — enables budget-share dues. */
	parValue?: boolean;
	/**
	 * Sum of the association's dues lines. In par-value mode each line holds an
	 * ANNUAL budget figure per GL code, so their total is the annual operating
	 * budget — see `annualBudgetCents` for why this outranks the stored column.
	 */
	linesTotalCents?: number;
	/**
	 * Annual operating budget in cents, hand-typed. Superseded by
	 * `linesTotalCents`; still read for associations configured before the
	 * budget moved onto the GL lines.
	 */
	operatingBudgetCents?: number;
	/**
	 * LEGACY: the old `round_dues_to_dollar` toggle, which meant round UP (#232). Read only
	 * when `rounding` is absent — e.g. a response from an API that predates it.
	 */
	roundToDollar?: boolean;
	/** Par-value rounding — see `DuesRounding`. Outranks `roundToDollar`. */
	rounding?: DuesRounding;
	/**
	 * The GL template's line amounts (annual, in par-value mode), in template order. With
	 * rounding on, each line is rounded separately and `ResolvedDues.lineCents` carries the
	 * per-line amounts — the caller posts THOSE, not a prorated split of the total.
	 */
	lineWeightsCents?: number[];
}

/** The effective par-value rounding: the explicit setting, else what the legacy flag meant. */
export const effectiveRounding = (assoc: AssociationDues): DuesRounding =>
	assoc.rounding ?? (assoc.roundToDollar ? 'up' : 'none');

/**
 * The annual budget par-value dues divide up.
 *
 * The per-GL dues lines are the source of truth: in par-value mode each line is
 * that GL code's annual budget, so the total IS the annual operating budget and
 * there is no separately-typed number to drift from it. `operatingBudgetCents`
 * remains the fallback for associations set up before that change.
 *
 * Returns undefined when neither is configured, which the caller must treat as
 * "no budget" — distinct from a budget of zero.
 */
export function annualBudgetCents(assoc: AssociationDues): number | undefined {
	if (assoc.linesTotalCents != null && assoc.linesTotalCents > 0) return assoc.linesTotalCents;
	return assoc.operatingBudgetCents ?? undefined;
}

/**
 * Narrow an `/association-dues` GET response down to what the resolver reads.
 * Every caller goes through this rather than spreading the fields by hand — the
 * previous copy-paste is how `linesTotalCents` would get missed at one site and
 * silently resolve dues against a stale budget there.
 */
export function duesFromResponse(dues: AssociationDues | undefined | null): AssociationDues {
	return {
		amountCents: dues?.amountCents,
		cadence: dues?.cadence,
		dayOfMonth: dues?.dayOfMonth,
		startOn: dues?.startOn,
		parValue: dues?.parValue,
		linesTotalCents: dues?.linesTotalCents,
		operatingBudgetCents: dues?.operatingBudgetCents,
		roundToDollar: dues?.roundToDollar,
		rounding: dues?.rounding,
		lineWeightsCents: dues?.lineWeightsCents,
	};
}

/** True when par-value dues can actually be computed — mode on AND a budget set. */
export function isParValueActive(dues: AssociationDues | undefined | null): boolean {
	return !!dues?.parValue && annualBudgetCents(duesFromResponse(dues)) != null;
}

/**
 * True when an association can bill dues — mirrors what `resolveAccountDues` can resolve to a
 * non-zero amount: a positive flat amount, or par value over a positive budget (the GL dues
 * lines' total, or the operating budget). Par-value communities usually carry no flat amount —
 * Hunter's Crossing bills from two GL lines — so a flat-amount-only test calls them unconfigured
 * forever. Outside par value the lines are only split WEIGHTS, so they configure nothing alone.
 * Shared by the onboarding checklist and the accounting tab, which once disagreed.
 */
export function isDuesConfigured(c: DuesConfigInput): boolean {
	return (c.duesAmountCents ?? 0) > 0 || isParValueBilling(c);
}

/**
 * True when dues bill by par value: the flag is on AND a positive budget exists. With the flag
 * on but no budget, `resolveAccountDues` falls back to the flat amount — so that is what to show.
 */
export function isParValueBilling(c: DuesConfigInput): boolean {
	return !!c.parValue && ((c.duesLineTotalCents ?? 0) > 0 || (c.operatingBudgetCents ?? 0) > 0);
}

export interface DuesConfigInput {
	duesAmountCents?: number | null;
	/** Sum of the active (unpinned, undeleted) dues lines' amounts. */
	duesLineTotalCents?: number | null;
	parValue?: boolean | null;
	operatingBudgetCents?: number | null;
}

export type DuesSource ='override' | 'par_value' | 'percent' | 'association' | 'none';

export interface ResolvedDues {
	amountCents: number;
	source: DuesSource;
	/** When source === 'percent', the applied percentage as bps (10000 = 100%). */
	percentBps?: number;
	/** When source === 'par_value', the ownership share as bps (10000 = 100%). */
	parValueBps?: number;
	/** When source === 'par_value', the annual budget the share was taken from. */
	annualBudgetCents?: number;
	/** When source === 'par_value', this account's share of that budget for a full year. */
	annualDuesCents?: number;
	/**
	 * When par value rounds per GL line: each line's amount, aligned with the
	 * `lineWeightsCents` it was given (0 for a non-positive line). Sums to `amountCents`.
	 */
	lineCents?: number[];
}

/** Number of dues postings per year for a cadence (monthly=12, quarterly=4, annual=1). */
export function periodsPerYear(cadence?: AssessmentCadence): number {
	return cadence === 'quarterly' ? 4 : cadence === 'annual' ? 1 : 12;
}

/**
 * Round a cents amount UP to the next whole dollar (next 100 cents).
 *
 * Deliberately `ceil`, not `round`. Par-value dues divide an ADOPTED budget the
 * board has to fund, so a per-account amount that rounds down leaves the
 * association collecting less than it approved — invisibly, because each owner
 * is short by under 50c. Westminster's 30 units came to $7.75 under budget ÷ 12
 * every month ($93/yr). Rounding up can only ever overshoot, which the roster's
 * reconciliation footer surfaces and a board can absorb; undershooting is a
 * shortfall nobody budgeted for.
 */
export function ceilCentsToDollar(cents: number): number {
	return Math.ceil(cents / 100) * 100;
}

/**
 * How far a par-value roster's dues total may legitimately sit from
 * budget ÷ periods, in cents. Asymmetric on purpose.
 */
export interface DuesDriftTolerance {
	/** Cents the roster may exceed budget ÷ periods by. */
	overCents: number;
	/** Cents the roster may fall SHORT of budget ÷ periods by. */
	underCents: number;
}

/**
 * Tolerance for the Par Value roster's reconciliation footer.
 *
 * Rounding is applied per ACCOUNT, so the allowance scales with the roster or
 * the report cries wolf on every rounded association — 30 units × up to 99c is
 * $30 of entirely correct rounding against a flat $1 threshold.
 *
 * It is asymmetric because `ceilCentsToDollar` only ever rounds UP: an
 * over-collection is explainable rounding, but a SHORTFALL cannot be rounding
 * at all. Under-collection means an account with no `parValueBps`, a
 * `duesOverrideAmountCents` below its share, or shares that don't reach 100% —
 * each a real problem, and each one a symmetric tolerance would silently
 * absorb up to N dollars of.
 *
 * With rounding off, each account rounds to the nearest cent and can err in
 * either direction, so the allowance goes back to symmetric.
 */
export function duesDriftTolerance(
	accountCount: number,
	/** The rounding mode, or the legacy boolean (true = up). */
	rounding?: boolean | DuesRounding,
	/** GL lines each account's dues are rounded across — every line rounds on its own. */
	lineCount = 1,
): DuesDriftTolerance {
	const mode: DuesRounding = rounding === true ? 'up' : rounding === false || rounding === undefined ? 'none' : rounding;
	const roundings = accountCount * Math.max(1, lineCount);
	if (mode === 'none') {
		const slack = Math.max(100, accountCount);
		return { overCents: slack, underCents: slack };
	}
	if (mode === 'nearest') {
		// Each rounded line is within 50c of its exact share, either way.
		const slack = Math.max(100, roundings * 50);
		return { overCents: slack, underCents: slack };
	}
	return { overCents: Math.max(100, roundings * 100), underCents: 100 };
}

/**
 * True when a roster's dues total is far enough from budget ÷ periods that the
 * report should flag it. `duesOffCents` is total − expected: positive is
 * over-collecting, negative is a shortfall.
 */
export function isDuesTotalOffBudget(duesOffCents: number, tolerance: DuesDriftTolerance): boolean {
	return duesOffCents > tolerance.overCents || duesOffCents < -tolerance.underCents;
}

/** Millionths of a percent in 100%: a share of PAR_MICRO_PCT_WHOLE is the whole budget. */
const PAR_MICRO_PCT_WHOLE = 100_000_000;
const PAR_DENOMINATOR = BigInt(PAR_MICRO_PCT_WHOLE);

/** num/den cents, rounded half-up to a cent. Exact — no float, so a .5 can't wobble. */
const roundToCent = (num: bigint, den: bigint): number => Number((2n * num + den) / (2n * den));

/** num/den cents, rounded to a whole dollar (in cents) — half-up for `nearest`, ceil for `up`. */
const roundToDollar = (num: bigint, den: bigint, rounding: Exclude<DuesRounding, 'none'>): number => {
	const perDollar = den * 100n;
	const dollars = rounding === 'up'
		? (num + perDollar - 1n) / perDollar
		: (2n * num + perDollar) / (2n * perDollar);
	return Number(dollars) * 100;
};

/**
 * Resolve the effective per-period dues for an account. Order:
 *   1. account.duesOverrideAmountCents  — explicit fixed amount
 *   2. par value (assoc.parValue + budget + account.parValueBps) — share of budget
 *   3. account.duesOverridePercentBps   — % of the association's default
 *   4. association.amountCents          — flat default for the association
 *   5. none                             — 0 cents, nothing configured
 *
 * Returns both the resolved cents and which rule fired so the UI can
 * explain the number ("$105.00 — 105% of association default").
 */
export function resolveAccountDues(account: AccountDuesInput, assoc: AssociationDues): ResolvedDues {
	if (account.duesOverrideAmountCents != null) {
		return { amountCents: account.duesOverrideAmountCents, source: 'override' };
	}
	const budget = annualBudgetCents(assoc);
	if (assoc.parValue && budget != null && account.parValueBps != null) {
		const periods = periodsPerYear(assoc.cadence);
		const microPct = parBpsToMicroPct(account.parValueBps);
		const rounding = effectiveRounding(assoc);
		const base = {
			source: 'par_value' as const,
			parValueBps: account.parValueBps,
			annualBudgetCents: budget,
			annualDuesCents: Math.round((budget * microPct) / PAR_MICRO_PCT_WHOLE),
		};
		const weights = assoc.lineWeightsCents ?? [];
		const weightTotal = weights.reduce((sum, w) => sum + Math.max(0, w), 0);
		if (rounding !== 'none' && weightTotal > 0) {
			// Each GL line's share of THIS period: budget × (w / Σw) × share ÷ periods.
			const lineCents = weights.map((w) => (w > 0
				? roundToDollar(BigInt(budget) * BigInt(w) * BigInt(microPct), BigInt(weightTotal) * PAR_DENOMINATOR * BigInt(periods), rounding)
				: 0));
			return { ...base, amountCents: lineCents.reduce((sum, c) => sum + c, 0), lineCents };
		}
		const num = BigInt(budget) * BigInt(microPct);
		const den = PAR_DENOMINATOR * BigInt(periods);
		return {
			...base,
			amountCents: rounding === 'none' ? roundToCent(num, den) : roundToDollar(num, den, rounding),
		};
	}
	if (account.duesOverridePercentBps != null && assoc.amountCents != null) {
		return {
			amountCents: Math.round((assoc.amountCents * account.duesOverridePercentBps) / 10000),
			source: 'percent',
			percentBps: account.duesOverridePercentBps,
		};
	}
	if (assoc.amountCents != null) {
		return { amountCents: assoc.amountCents, source: 'association' };
	}
	return { amountCents: 0, source: 'none' };
}
